/**
 * Choose a new password from an emailed reset link (/reset-password?token=…).
 *
 * The token is the credential (app/helpers/passwords.py: signed, one hour,
 * single-use). It is read once and then stripped from the address bar, so
 * it does not linger in history or ride a Referer. The server holds the
 * password rules; the length check here only saves a round trip.
 */
import { useState } from 'react'

import { ApiError, api } from '../../lib/api'
import './auth.css'

/** Mirrors passwords.MIN_LENGTH; the server is the authority. */
export const MIN_PASSWORD_LENGTH = 12

function takeToken(): string | null {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if (token) {
    params.delete('token')
    const rest = params.toString()
    window.history.replaceState(null, '',
      window.location.pathname + (rest ? `?${rest}` : ''))
  }
  return token
}

export default function ResetPassword() {
  const [token] = useState(takeToken)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    try {
      await api('/api/v1/auth/reset-password', {
        method: 'POST', body: JSON.stringify({ token, password }),
      })
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Priori</div>
        <h1 className="auth-title">Choose a new password</h1>

        {!token ? (
          <div className="auth-flash auth-flash--error">
            This page needs the link from your reset email.
          </div>
        ) : done ? (
          <div className="auth-flash auth-flash--success">
            Your password is updated. Sign in with it now.
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)}>
            <p className="auth-sub">At least {MIN_PASSWORD_LENGTH} characters.</p>
            {error && <div className="auth-flash auth-flash--error">{error}</div>}
            <label className="auth-field">
              <span className="auth-label">New password</span>
              <input className="auth-input" type="password" required
                     autoComplete="new-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} />
            </label>
            <label className="auth-field">
              <span className="auth-label">Confirm new password</span>
              <input className="auth-input" type="password" required
                     autoComplete="new-password" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} />
            </label>
            <button className="auth-btn auth-btn--primary" type="submit" disabled={busy}>
              Set password
            </button>
          </form>
        )}

        <div className="auth-links">
          {token && !done
            ? <a href="/forgot-password">Ask for a new link</a>
            : <a href="/login">Back to sign in</a>}
        </div>
      </div>
    </div>
  )
}
